import { useEffect, useRef, useState } from 'react'
import { Bell, X, Check, ArrowRight, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { io } from 'socket.io-client'
import api from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

// Notif-accent → HUD-token map. Vroeger stonden hier bg-pink/emerald/etc.
// wat naast de rest van de HUD een vreemde eend was en niet mee-swapte met
// de dark/light-theme.
const ACCENT_BG = {
  gold:    'bg-gold',
  emerald: 'bg-live',
  green:   'bg-live',
  magenta: 'bg-peach',
  rose:    'bg-peach',
  red:     'bg-signal',
  orange:  'bg-gold',
  blue:    'bg-acid',
  cyan:    'bg-acid',
  purple:  'bg-acid',
  slate:   'bg-muted-foreground',
}

export default function NotificationBell({ variant = 'header' }) {
  const [items, setItems]   = useState([])
  const [unread, setUnread] = useState(0)
  const [open, setOpen]     = useState(false)
  const [dropdownPos, setDropdownPos] = useState(null)
  const ref = useRef(null)
  const btnRef = useRef(null)

  const load = async () => {
    try {
      const r = await api.get('/notifications/mine')
      setItems(r.data.notifications ?? [])
      setUnread(r.data.unread_count ?? 0)
    } catch {}
  }

  useEffect(() => {
    if (!localStorage.getItem('auth_token')) return
    load()
    const iv = setInterval(load, 45_000)  // polling safety net

    const s = io({
      auth: { token: localStorage.getItem('auth_token') },
      transports: ['polling', 'websocket'],
      reconnection: true,
    })
    s.on('notification:new', (n) => {
      setItems(prev => [n, ...prev].slice(0, 50))
      setUnread(prev => prev + 1)
    })
    return () => { clearInterval(iv); s.disconnect() }
  }, [])

  // Close op click buiten
  useEffect(() => {
    if (!open) return
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  // Sidebar-variant: bereken absolute viewport-positie zodat de dropdown
  // buiten de sidebar's overflow-hidden context kan renderen. Zonder
  // `position: fixed` clipt de sidebar de 320px-brede dropdown weg.
  useEffect(() => {
    if (!open || variant !== 'sidebar' || !btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    // Linker-onderhoek van dropdown vastpinnen aan de bel: dropdown hangt
    // dus omhoog vanaf de bel, met een klein gap ernaast. Gebruikt bottom
    // ipv top zodat de plakhoek stabiel is ongeacht dropdown-hoogte.
    setDropdownPos({
      left:   rect.right + 4,
      bottom: window.innerHeight - rect.bottom,
    })
  }, [open, variant])

  const markRead = async (id) => {
    await api.post(`/notifications/mine/${id}/read`)
    setItems(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
    setUnread(prev => Math.max(0, prev - 1))
  }
  const markAllRead = async () => {
    await api.post('/notifications/mine/read-all')
    setItems(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })))
    setUnread(0)
  }
  const wisEen = async (id) => {
    await api.delete(`/notifications/mine/${id}`)
    setItems(prev => {
      const n = prev.find(x => x.id === id)
      if (n && !n.read_at) setUnread(u => Math.max(0, u - 1))
      return prev.filter(x => x.id !== id)
    })
  }
  const wisGelezen = async () => {
    await api.delete('/notifications/mine?onlyRead=true')
    setItems(prev => prev.filter(n => !n.read_at))
  }
  const wisAlles = async () => {
    if (!window.confirm('Alle notificaties wissen? (ongelezen worden ook verwijderd)')) return
    await api.delete('/notifications/mine')
    setItems([]); setUnread(0)
  }

  return (
    <div className="relative" ref={ref}>
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        className="relative h-9 w-9 rounded-md hover:bg-accent flex items-center justify-center transition-colors"
        aria-label="Notificaties"
      >
        <Bell className={`h-5 w-5 ${unread > 0 ? 'text-signal' : ''}`} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-signal text-white text-[10px] font-bold flex items-center justify-center notif-pulse">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`w-80 max-h-[70vh] overflow-hidden bg-background border border-border rounded-lg shadow-lg z-50 flex flex-col ${
            variant === 'sidebar' ? 'fixed' : 'absolute right-0 mt-2'
          }`}
          style={variant === 'sidebar' && dropdownPos ? {
            left: dropdownPos.left, bottom: dropdownPos.bottom,
          } : undefined}
        >
          <div className="flex items-center justify-between p-3 border-b border-border gap-2 flex-wrap">
            <div className="font-semibold">Notificaties {unread > 0 && <Badge variant="outline" className="ml-1 text-xs">{unread} nieuw</Badge>}</div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <Button variant="ghost" size="sm" onClick={markAllRead}
                        title="Markeer alles als gelezen">
                  <Check className="h-3 w-3 mr-1" /> Gelezen
                </Button>
              )}
              {items.some(n => n.read_at) && (
                <Button variant="ghost" size="sm" onClick={wisGelezen}
                        title="Wis alle gelezen notificaties">
                  <Trash2 className="h-3 w-3 mr-1" /> Wis gelezen
                </Button>
              )}
              {items.length > 0 && (
                <Button variant="ghost" size="sm" onClick={wisAlles}
                        title="Wis alles (ook ongelezen)"
                        className="text-signal hover:text-signal">
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {items.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">Geen notificaties</div>
            )}
            {items.map(n => (
              <NotifRow key={n.id} n={n}
                        onRead={() => markRead(n.id)}
                        onDelete={() => wisEen(n.id)}
                        onClose={() => setOpen(false)} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function NotifRow({ n, onRead, onDelete, onClose }) {
  const isNew = !n.read_at
  const dot = ACCENT_BG[n.accent] || 'bg-acid'
  const Row = (
    <div className={`group p-3 border-b border-border/50 last:border-0 hover:bg-accent/40 transition-colors relative ${isNew ? 'bg-accent/20' : ''}`}
         onClick={() => { if (isNew) onRead(); }}>
      <div className="flex gap-2">
        <div className={`h-2 w-2 rounded-full ${isNew ? dot : 'bg-transparent'} mt-1.5 shrink-0`} />
        <div className="flex-1 min-w-0 pr-6">
          <div className="font-semibold text-sm truncate">{n.title}</div>
          {n.message && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</div>}
          <div className="text-[10px] text-muted-foreground mt-1">
            {new Date(n.created_at).toLocaleString('nl-BE', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
          </div>
        </div>
        {n.action_url && <ArrowRight className="h-4 w-4 text-muted-foreground self-center" />}
      </div>
      {onDelete && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete() }}
          className="absolute top-2 right-2 p-1 rounded text-muted-foreground/40 hover:text-signal hover:bg-signal/10 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Wis deze notificatie">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  )
  return n.action_url
    ? <Link to={n.action_url} onClick={onClose} className="block">{Row}</Link>
    : Row
}
